import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../translations';
import { API_BASE } from '../../config';
import { formatYMD } from '../../utils/contract';
import { getInventorySettingsStorageKey } from '../../utils/inventoryStorage';
import DatePicker from 'react-datepicker';
import PaginationCompact from '../../components/PaginationCompact';
import { useConfirm } from '../../components/ui/Modal';
import { registerLocale } from 'react-datepicker';
import pl from 'date-fns/locale/pl';
import 'react-datepicker/dist/react-datepicker.css';

import {
  PackageIcon, PlusIcon, SearchIcon, TrashIcon, GearIcon, CalendarIcon,
  ChevronDownIcon, DotIcon, BarcodeIcon, sortIcons,
} from './icons';
import {
  Container, TableContainer, PrimaryButton, SecondaryButton,
  DatepickerTheme, StatusBadge, SettingsButton,
  HeaderRow, TitleSection, TitleIcon, Title, ActionButtons, DangerButton,
  SelectionActionBar, SelectionMeta, SelectionActions,
  LoaderOverlay, Spinner, Tooltip, ThWithTooltip,
  UndoNotification, UndoButton, SearchInput,
  FilterBar, FilterBarTop, FilterSearch, FilterResults, FilterPills,
  FilterClearAll, PillDateSlot, ScanBadge,
  TableWrapper, Table, Th, Td, EquipmentTableRow, EquipmentCheckbox,
  SelectionCell, SelectionToggleButton, SortButton, EmptyState,
  StatsRow, StatCard, StatIcon, StatContent, StatValue, StatLabel,
  ErrorBanner, ErrorMsg,
} from './inventory.styles';
import {
  startOfDay, equipmentColumns, BASE_INVENTORY_COLUMN_KEYS,
  resolveInventoryValue, defaultVisibleColumns,
  loadInventorySettings, formatDailyPrice, itemMatchesClass, sortValueForColumn,
  withEmptyDash, buildFilterDimensions, activeFilterSummary, clearFilterPatch,
  countActiveFilters, TOP_LEVEL_FILTER_KEYS, PRZYPIETE_KOLUMNY_MEDIA, kolumnyZNazwaNaPoczatku,
  customTableColumns, dynamicFilterType, columnLabel, formatCustomCell,
} from './utils';
import useBarcodeScan from '../../utils/useBarcodeScan';
import useMediaQuery from '../../utils/useMediaQuery';
import ScanSources from '../../components/scanner/ScanSources';
import { findEquipmentByCode, parseSystemCode } from '../../utils/equipmentCode';
import { PrinterIcon } from '../../ui/icons';
import FilterPill from './components/FilterPill';
import InlineRangeEditor from './components/InlineRangeEditor';
import { typeLabel, equipmentStatusLabel } from '../../utils/dictionaryLabel';

registerLocale('pl', pl);


// Usuń niepotrzebne stany i efekty:
export default function Inventory() {
  // W.5: UX-only gate (server enforces admin on the import endpoints) — reuses the
  // localStorage 'isAdmin' pattern from pages/profile.js.
  const isAdminUser = localStorage.getItem('isAdmin') === '1' || localStorage.getItem('isSuperAdmin') === '1';

  const defaultFilters = {
    // Jedno pole szukania zamiast dwóch (nazwa + numer). Szuka po nazwie, marce i numerze
    // inwentarzowym — patrz `matchesFilters`. Dwa osobne pola były ŁĄCZONE spójnikiem „i",
    // więc wpisanie nazwy w jedno i numeru w drugie potrafiło nie znaleźć niczego.
    search: '',
    dateFrom: null,
    dateTo: null,
    size: '',
    minSize: '',
    maxSize: '',
    brand: '',
    category: '',
    class: '',
    status: '',
    dynamic: {}
  };

  const [equipment, setEquipment] = useState([]);
  const [filters, setFilters] = useState(defaultFilters);
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [sort, setSort] = useState({ key: '', direction: 'none' });
  const [deleteError, setDeleteError] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [deletedItems, setDeletedItems] = useState([]);
  const [searchDebounce, setSearchDebounce] = useState('');
  // Ostatnio zeskanowany kod — wyłącznie po to, żeby POKAZAĆ, że czytnik zadziałał.
  // Bez tego skan wyglądałby jak samoczynne wpisanie się tekstu w pole szukania.
  const [lastScan, setLastScan] = useState(null);

  const [availableEquipmentIds, setAvailableEquipmentIds] = useState(null);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);

  const savedSettings = loadInventorySettings();
  const [visibleColumns, setVisibleColumns] = useState(savedSettings.visibleColumns || defaultVisibleColumns);
  // Pola własne firmy z Ustawień magazynu (utils.js#customTableColumns): bez nich włączona
  // w ustawieniach kolumna własna nie pojawiała się w tabeli w ogóle.
  const [customColumnsConfig, setCustomColumnsConfig] = useState(savedSettings.customColumns || []);
  const customColumns = useMemo(() => customTableColumns(customColumnsConfig), [customColumnsConfig]);
  const customFieldTypeByKey = useMemo(
    () => Object.fromEntries(customColumns.map(c => [c.key, c.fieldType])),
    [customColumns]
  );
  const filterTypeFor = (key) => dynamicFilterType(key, customFieldTypeByKey[key]);

  const dynamicFilterColumns = useMemo(
    () => [...equipmentColumns, ...customColumns]
      .filter(c => visibleColumns.includes(c.key) && !BASE_INVENTORY_COLUMN_KEYS.has(c.key)),
    [visibleColumns, customColumns]
  );

  const navigate = useNavigate();
  const { t } = useTranslation();
  const confirm = useConfirm();

  // Czytnik kodów: zeskanowanie czegokolwiek na tej stronie wpisuje kod do szukajki, która
  // dopasowuje także kod kreskowy/QR — więc sprzęt zostaje na liście sam. To jest cały
  // przypadek „klient przy ladzie, skanujesz naklejkę i patrzysz, co to za sztuka”.
  // Aparat i telefon (ScanSources) wołają TO SAMO; odpowiedź trafia na podgląd aparatu i pasek
  // telefonu, bo osoba skanująca patrzy wtedy na naklejkę, a nie na listę.
  const handleScan = (kod) => {
    setSearchDebounce(kod);
    setPage(1);
    setLastScan(kod);
    const hit = findEquipmentByCode(kod, equipment);
    return hit
      ? { tone: 'ok', text: t('inventory.scanFound').replace('{name}', hit.name) }
      : { tone: 'warn', text: t('inventory.scanNotFound').replace('{code}', kod) };
  };
  useBarcodeScan(handleScan);

  // Znacznik „zeskanowano” gasi się sam; zostawiony na stałe myliłby przy ręcznej zmianie
  // treści szukania (kod byłby nadal wypisany, choć nikt już go nie szuka).
  useEffect(() => {
    if (!lastScan) return undefined;
    const id = setTimeout(() => setLastScan(null), 4000);
    return () => clearTimeout(id);
  }, [lastScan]);

  // Auto-hide deleteError after 10 seconds
  useEffect(() => {
    if (!deleteError) return;
    const id = setTimeout(() => setDeleteError(null), 10000);
    return () => clearTimeout(id);
  }, [deleteError]);

  // Debounce dla pola search
  useEffect(() => {
    const timeout = setTimeout(() => {
      setFilters(prev => ({ ...prev, search: searchDebounce }));
    }, 400);
    return () => clearTimeout(timeout);
  }, [searchDebounce]);

  // Debounce dla pola inventory number

  // Auto-hide undo notification
  useEffect(() => {
    if (deletedItems.length === 0) return;
    const timeout = setTimeout(() => {
      setDeletedItems([]);
    }, 8000);
    return () => clearTimeout(timeout);
  }, [deletedItems]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const settingsKey = getInventorySettingsStorageKey();
    const onStorage = (e) => {
      if (e.key === settingsKey) {
        const next = loadInventorySettings();
        if (next.visibleColumns && next.visibleColumns.length > 0) {
          setVisibleColumns(next.visibleColumns);
        }
        setCustomColumnsConfig(Array.isArray(next.customColumns) ? next.customColumns : []);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  useEffect(() => {
    setFilters(prev => {
      const prevDynamic = prev.dynamic || {};
      const allowed = new Set(dynamicFilterColumns.map(c => c.key));
      const nextDynamic = {};
      Object.keys(prevDynamic).forEach(key => {
        if (allowed.has(key)) nextDynamic[key] = prevDynamic[key];
      });
      return { ...prev, dynamic: nextDynamic };
    });
  }, [dynamicFilterColumns]);

  // Ładowanie dostępnych sprzętów w wybranym okresie
  useEffect(() => {
    const from = filters.dateFrom;
    const to = filters.dateTo;

    if (!from || !to) {
      setAvailableEquipmentIds(null);
      setAvailabilityLoading(false);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setAvailabilityLoading(true);
      try {
        // Lokalny dzień, nie toISOString(): północ czasu polskiego to w UTC poprzedni dzień,
        // więc filtr „dostępny od 20.09" pytał serwer o 19.09 (i ukrywał sprzęt wolny od 20.).
        const params = new URLSearchParams({
          start: formatYMD(startOfDay(from)),
          end: formatYMD(startOfDay(to))
        });
        const res = await axios.get(`${API_BASE}/api/equipment/available?${params.toString()}`);
        if (cancelled) return;
        const normalize = (v) => {
          if (v == null) return v;
          if (typeof v === 'number') return v;
          if (typeof v === 'string' && /^\d+$/.test(v)) return Number(v);
          return v;
        };
        const ids = new Set((res.data || []).map(e => normalize(e.id)));
        setAvailableEquipmentIds(ids);
      } catch (err) {
        console.error('Błąd pobierania dostępnego sprzętu:', err);
        if (!cancelled) setAvailableEquipmentIds(null);
      } finally {
        if (!cancelled) setAvailabilityLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [filters.dateFrom, filters.dateTo]);

  const matchesFilters = (item, activeFilters, skipKey) => {
    // Dostępność w okresie
    if (skipKey !== 'availability') {
      if (activeFilters.dateFrom && activeFilters.dateTo) {
        // w trakcie ładowania nie filtruj, żeby nie robić "migania" pustą listą
        if (!availabilityLoading && availableEquipmentIds instanceof Set) {
          const normalize = (v) => {
            if (v == null) return v;
            if (typeof v === 'number') return v;
            if (typeof v === 'string' && /^\d+$/.test(v)) return Number(v);
            return v;
          };
          const id = normalize(item.id);
          if (!availableEquipmentIds.has(id)) return false;
        }
      }
    }

    if (skipKey !== 'search') {
      if (activeFilters.search) {
        // Jedno pole, trzy miejsca: przy ladzie pada „Atomic", „Redster" albo „A104"
        // i nikt nie zastanawia się, do której rubryki to wpisać.
        const search = activeFilters.search.trim().toLowerCase();
        // Kod kreskowy/QR doszedł 2026-09-23: bez niego zeskanowanie naklejki ze sprzętu
        // nie znajdowało niczego, bo kod leży w `metadata`, a nie w kolumnie — stąd
        // `resolveInventoryValue`, a nie zwykły odczyt pola.
        // Kod systemowy `RNT-<id>` z naklejki sztuki bez numeru (utils/equipmentCode.js) nie
        // występuje w żadnym polu — rozpoznajemy go po identyfikatorze, dokładnie.
        const systemId = parseSystemCode(search);
        if (systemId != null) {
          if (Number(item.id) !== systemId) return false;
        } else {
          const gdzieSzukamy = [
            item.name,
            item.brand,
            item.inventory_number,
            resolveInventoryValue(item, 'barcode'),
          ].map(v => String(v == null ? '' : v).toLowerCase());
          if (!gdzieSzukamy.some(v => v.includes(search))) return false;
        }
      }
    }

    if (skipKey !== 'size') {
      if (activeFilters.minSize || activeFilters.maxSize) {
        const sizeNum = parseFloat(item.size);
        if (!Number.isFinite(sizeNum)) return false;
        const min = parseFloat(activeFilters.minSize);
        const max = parseFloat(activeFilters.maxSize);
        const hasMin = Number.isFinite(min);
        const hasMax = Number.isFinite(max);
        if (hasMin && sizeNum < min) return false;
        if (hasMax && sizeNum > max) return false;
      } else if (activeFilters.size) {
        const sizeNum = parseFloat(item.size);
        if (!Number.isFinite(sizeNum)) return false;
        const filterSize = parseFloat(activeFilters.size);
        if (!Number.isFinite(filterSize)) return false;
        if (Math.abs(sizeNum - filterSize) > 0.0001) return false;
      }
    }

    // Domyślnie ukrywamy sprzęt nieaktywny
    if (item.status === 'Nieaktywny') {
      // jeśli filtr status jest ustawiony na Nieaktywny, dopuść
      if (activeFilters.status !== 'Nieaktywny') {
        return false;
      }
    }

    if (skipKey !== 'status') {
      if (activeFilters.status && item.status !== activeFilters.status) return false;
    }

    if (skipKey !== 'brand') {
      if (activeFilters.brand && item.brand !== activeFilters.brand) return false;
    }

    if (skipKey !== 'category') {
      if (activeFilters.category && item.category !== activeFilters.category) return false;
    }

    if (skipKey !== 'class') {
      if (!itemMatchesClass(item, activeFilters.class)) return false;
    }

    const dynamic = activeFilters.dynamic || {};
    for (const [key, expected] of Object.entries(dynamic)) {
      const expectedStr = String(expected ?? '').trim();
      if (!expectedStr) continue;
      const value = resolveInventoryValue(item, key);
      const type = filterTypeFor(key);

      if (type === 'date') {
        const itemDate = value ? String(value).slice(0, 10) : '';
        if (itemDate !== expectedStr) return false;
        continue;
      }

      if (type === 'number') {
        const itemNum = Number(value);
        const expectedNum = Number(expectedStr);
        if (!Number.isFinite(itemNum) || !Number.isFinite(expectedNum) || itemNum !== expectedNum) return false;
        continue;
      }

      const valueText = Array.isArray(value)
        ? value.join(', ').toLowerCase()
        : String(value ?? '').toLowerCase();
      if (!valueText.includes(expectedStr.toLowerCase())) return false;
    }

    return true;
  };

  // Lista unikalnych, dostępnych numerów rozmiarów (np. 40, 40.5, 41)
  const sizeOptions = useMemo(() => {
    const filtered = equipment.filter(e => matchesFilters(e, filters, 'size'));
    const numericSizes = filtered
      .map(e => parseFloat(e.size))
      .filter(s => Number.isFinite(s));

    if (!numericSizes.length) return [];

    const uniq = Array.from(new Set(numericSizes)).sort((a, b) => a - b);
    return uniq.map(s => ({ value: String(s), label: s % 1 === 0 ? String(s) : String(s) }));
  }, [equipment, filters]);

  const brandOptions = useMemo(() => {
    const set = new Set();
    equipment.forEach(e => {
      if (e.brand && matchesFilters(e, filters, 'brand')) set.add(e.brand);
    });
    return [...set];
  }, [equipment, filters]);

  const categoryOptions = useMemo(() => {
    const set = new Set();
    equipment.forEach(e => {
      if (matchesFilters(e, filters, 'category')) set.add(e.category);
    });
    return [...set];
  }, [equipment, filters]);

  const classOptions = useMemo(() => {
    const set = new Set();
    equipment.forEach(e => {
      if (matchesFilters(e, filters, 'class')) set.add(e.class);
    });
    return [...set];
  }, [equipment, filters]);

  // Opcje dla pól dodatkowych - dynamicznie generowane z unikalnych wartości
  const dynamicFieldOptions = useMemo(() => {
    const optionsMap = {};
    dynamicFilterColumns.forEach(col => {
      const type = filterTypeFor(col.key);
      if (type === 'date' || type === 'number') {
        // dla dat i liczb nie generujemy opcji dropdown - pozostawimy input
        return;
      }
      const valuesSet = new Set();
      equipment.forEach(e => {
        const value = resolveInventoryValue(e, col.key);
        if (Array.isArray(value)) {
          value.forEach(v => { const strVal = String(v ?? '').trim(); if (strVal) valuesSet.add(strVal); });
        } else if (value != null && typeof value !== 'object') {
          const strVal = String(value).trim();
          if (strVal) valuesSet.add(strVal);
        }
      });
      optionsMap[col.key] = [
        { value: '', label: columnLabel(t, col) },
        ...Array.from(valuesSet).sort().map(v => ({ value: v, label: v }))
      ];
    });
    return optionsMap;
  }, [equipment, dynamicFilterColumns, customFieldTypeByKey, t]);

  useEffect(() => {
    const fetchEquipment = async () => {
      setLoading(true);
      try {
        // Zaktualizuj statusy tylko raz przy załadowaniu. Błąd przeliczenia nie może ukryć listy:
        // sprzęt pokazujemy ze statusami zapisanymi w bazie.
        const csrfToken = localStorage.getItem('csrfToken') || '';
        try {
          await axios.post(`${API_BASE}/api/equipment/update-statuses`, {}, {
            withCredentials: true,
            headers: { 'X-CSRF-Token': csrfToken }
          });
        } catch (errStatusy) {
          console.error('update-statuses failed:', errStatusy);
        }

        // Pobierz sprzęt
        const res = await axios.get(`${API_BASE}/api/equipment`, { withCredentials: true });
        setEquipment(res.data);
      } catch (err) {
        console.error('Błąd pobierania sprzętu:', err);
        setEquipment([]);
      } finally {
        setLoading(false);
      }
    };

    fetchEquipment();

    // USUŃ interval - aktualizacja tylko przy montowaniu komponentu
    // const interval = setInterval(fetchEquipment, 30000);
    // return () => clearInterval(interval);
  }, []);

  const filteredEquipment = equipment.filter(e => matchesFilters(e, filters));

  // DODAJ STATYSTYKI:
  const stats = {
    available: equipment.filter(e => e.status === 'Dostępny').length,
    rented: equipment.filter(e => e.status === 'Wypożyczony').length,
    service: equipment.filter(e => e.status === 'W serwisie').length,
  };

  const sortedEquipment = [...filteredEquipment].sort((a, b) => {
    if (sort.direction === 'none' || !sort.key) return 0;
    const av = sortValueForColumn(a, sort.key);
    const bv = sortValueForColumn(b, sort.key);
    if (typeof av === 'number') {
      return sort.direction === 'asc' ? av - bv : bv - av;
    }
    // Brak wartości jako pusty tekst (dawniej „undefined”), liczby w tekście po kolei (2 < 10).
    const as = String(av ?? ''), bs = String(bv ?? '');
    return sort.direction === 'asc'
      ? as.localeCompare(bs, 'pl', { numeric: true })
      : bs.localeCompare(as, 'pl', { numeric: true });
  });

  const totalPages = Math.ceil(sortedEquipment.length / pageSize);
  const paginatedEquipment = sortedEquipment.slice((page - 1) * pageSize, page * pageSize);
  const selectedEquipment = useMemo(() => {
    if (selected.length !== 1) return null;
    return paginatedEquipment[selected[0]] || null;
  }, [selected, paginatedEquipment]);

  useEffect(() => {
    setSelected(prev => prev.filter(i => i >= 0 && i < paginatedEquipment.length));
  }, [paginatedEquipment.length]);

  const handleSelect = (idx) => {
    setSelected(selected.includes(idx)
      ? selected.filter(i => i !== idx)
      : [...selected, idx]);
  };

  const handleDelete = async () => {
    if (!selected.length) return;

    // Zbierz elementy do usunięcia
    const itemsToDelete = selected
      .map(idx => {
        const item = paginatedEquipment[idx];
        return item ? { ...item, originalIndex: idx } : null;
      })
      .filter(Boolean);

    if (!itemsToDelete.length) {
      setSelected([]);
      return;
    }

    const confirmMessage =
      itemsToDelete.length === 1
        ? t('inventory.confirmDeleteOne')
        : t('inventory.confirmDeleteMany', { count: itemsToDelete.length });

    const ok = await confirm({ title: confirmMessage, tone: 'danger', confirmLabel: t('common.delete') });
    if (!ok) return;

    setDeletedItems(itemsToDelete);
    setSelected([]);

    // Po 8 sekundach faktycznie usuń (jeśli nie cofnięto)
    setTimeout(async () => {
      setDeleting(true);
      setDeleteError(null);
      try {
        const csrfDel = localStorage.getItem('csrfToken') || '';
        for (const item of itemsToDelete) {
          try {
            await axios.delete(`${API_BASE}/api/equipment/${item.id}`, {
              withCredentials: true,
              headers: { 'X-CSRF-Token': csrfDel }
            });
          } catch (err) {
            const status = err?.response?.status;
            const serverMsg = err?.response?.data?.message || err.message || '';
            let userMessage = t('inventory.deleteFailed').replace('{name}', item.name || item.id);

            if (status === 404) {
              userMessage = t('inventory.deleteNotFound').replace('{name}', item.name || item.id);
            } else if (/powiązany|powi|foreign key|constraint|referenc/i.test(serverMsg)) {
              userMessage = t('inventory.deleteHasRefs').replace('{name}', item.name || item.id);
            } else if (/Nie można usunąć sprzętu|Nie można usunąć/i.test(serverMsg)) {
              userMessage = `${serverMsg}`;
            } else {
              userMessage = t('inventory.deleteError').replace('{name}', item.name || item.id).replace('{msg}', serverMsg);
            }

            setDeleteError(userMessage);
            throw new Error(userMessage);
          }
        }

        // Odśwież listę po pomyślnym usunięciu
        const res = await axios.get(`${API_BASE}/api/equipment`);
        setEquipment(res.data);
        setDeleteError(null);
        setDeletedItems([]);
      } catch (finalErr) {
        if (!deleteError) {
          setDeleteError(finalErr.message || t('inventory.deleteGenericError'));
        }
      } finally {
        setDeleting(false);
      }
    }, 8000);
  };

  const handleUndo = () => {
    setDeletedItems([]);
    setDeleteError(null);
  };

  const handleSort = key => {
    setSort(prev => ({
      key,
      direction:
        prev.key !== key ? 'asc'
        : prev.direction === 'asc' ? 'desc'
        : prev.direction === 'desc' ? 'none'
        : 'asc'
    }));
  };

  const handleOpenCalendar = () => {
    navigate(`/inventory/all/calendar`);
  };

  const handleEditSelected = () => {
    if (!selectedEquipment?.id) return;
    navigate(`/inventory/${selectedEquipment.id}`);
  };

  const handleClearSelection = () => {
    setSelected([]);
  };

  // Data na pigułce jest KRÓTKA („20.09"), bo pigułka ma mieścić się w rzędzie obok
  // kilku innych. Pełny rok widać w kalendarzu po otwarciu dymku.
  const formatFilterDate = (d) => {
    const data = d instanceof Date ? d : new Date(d);
    if (Number.isNaN(data.getTime())) return '';
    const dzien = String(data.getDate()).padStart(2, '0');
    const miesiac = String(data.getMonth() + 1).padStart(2, '0');
    return `${dzien}.${miesiac}`;
  };

  const handleClearFilters = () => {
    setFilters(defaultFilters);
    setSearchDebounce('');
    setPage(1);
  };

  // Zdjęcie JEDNEGO filtra krzyżykiem na pigułce. `clearFilterPatch` zwraca `null` dla pól
  // własnych — te żyją w zagnieżdżonym `filters.dynamic` i czyści je `setDynamicFilter`.
  const handleClearOneFilter = (key) => {
    const patch = clearFilterPatch(key);
    if (patch) setFilters(prev => ({ ...prev, ...patch }));
    else setDynamicFilter(key, '');
    setPage(1);
  };

  const setDynamicFilter = (key, value) => {
    setFilters(prev => ({
      ...prev,
      dynamic: {
        ...(prev.dynamic || {}),
        [key]: value
      }
    }));
  };

  // Wymiary paska filtrów wynikają z konfiguracji pól magazynu tej organizacji
  // (`visibleColumns`), nie z listy wpisanej na sztywno — patrz `utils.js#buildFilterDimensions`.
  const filterDimensions = useMemo(
    () => buildFilterDimensions({ visibleColumns, columns: [...equipmentColumns, ...customColumns] }),
    [visibleColumns, customColumns]
  );

  // Opcje per wymiar. Pigułka, która nie ma z czego wybierać, NIE renderuje się w ogóle
  // (patrz filtr niżej): pusty dymek to obietnica bez pokrycia, a przy świeżym magazynie
  // albo wąskiej branży takich wymiarów bywa połowa.
  const filterOptionsByKey = useMemo(() => {
    const mapa = {
      status: ['Dostępny', 'Wypożyczony', 'W serwisie', 'Nieaktywny']
        .map(v => ({ value: v, label: equipmentStatusLabel(t, v) })),
      category: categoryOptions.map(v => ({ value: v, label: typeLabel(t, v) })),
      brand: brandOptions.map(v => ({ value: v, label: v })),
      class: classOptions.map(v => ({ value: v, label: v })),
      size: sizeOptions,
    };
    Object.entries(dynamicFieldOptions).forEach(([key, opcje]) => {
      mapa[key] = (opcje || []).filter(o => String(o.value || '').trim() !== '');
    });
    return mapa;
  }, [categoryOptions, brandOptions, classOptions, sizeOptions, dynamicFieldOptions, t]);

  // Wymiary, które faktycznie mają co pokazać. Dostępność, daty i liczby nie mają listy
  // wartości, więc przechodzą zawsze; reszta musi mieć co najmniej jedną opcję w danych.
  const visibleFilterDimensions = useMemo(
    () => filterDimensions.filter((dim) => {
      if (dim.kind === 'availability' || dim.kind === 'date' || dim.kind === 'number') return true;
      if (dim.kind === 'size') return (filterOptionsByKey.size || []).length > 0;
      return (filterOptionsByKey[dim.key] || []).length > 0;
    }),
    [filterDimensions, filterOptionsByKey]
  );

  // Licznik idzie przez czysty helper (utils.js) — zakres dat liczy się jako JEDEN filtr,
  // bo na pasku jest jedną pigułką. Dawna wersja liczyła `dateFrom` i `dateTo` osobno.
  const activeFiltersCount = useMemo(
    () => countActiveFilters(filters, filterDimensions),
    [filters, filterDimensions]
  );


  const visibleColumnsConfig = useMemo(() => {
    const extraFieldsAsColumns = [
      { key: 'price', labelKey: 'addEquipment.price' },
      { key: 'bail', labelKey: 'addEquipment.bail' },
      { key: 'price_over_7', labelKey: 'addEquipment.priceOver7' }
    ];
    const allColumns = equipmentColumns
      .concat(extraFieldsAsColumns.filter(f => !equipmentColumns.some(ec => ec.key === f.key)))
      .concat(customColumns);
    // Kolejność stała (wbudowane, cenowe, na końcu własne). Kolejność z konfiguracji byłaby
    // kolejnością klikania przełączników, bo kolumn nie da się tam przestawiać.
    return allColumns.filter(c => visibleColumns.includes(c.key));
  }, [visibleColumns, customColumns]);

  // Na dotyku i wąskim ekranie tabela przypina pierwszą kolumnę z danymi, więc stoi w niej nazwa
  // (bez niej numer), nie Kategoria (utils.js#kolumnyZNazwaNaPoczatku, test na iPhonie 17).
  const przypietaKolumna = useMediaQuery(PRZYPIETE_KOLUMNY_MEDIA);
  const kolumnyTabeli = useMemo(
    () => (przypietaKolumna ? kolumnyZNazwaNaPoczatku(visibleColumnsConfig) : visibleColumnsConfig),
    [przypietaKolumna, visibleColumnsConfig]
  );

  const renderCell = (colKey, e) => {
    switch (colKey) {
      case 'category':
        return typeLabel(t, e.category);
      case 'brand':
        return withEmptyDash(e.brand);
      case 'name':
        return e.name;
      case 'size':
        return withEmptyDash(e.size);
      case 'inventory_number':
        return e.inventory_number;
      case 'class':
        return e.class;
      case 'tags': {
        const raw = resolveInventoryValue(e, 'tags');
        const tags = Array.isArray(raw) ? raw : (typeof raw === 'string' ? raw.split(',') : []);
        return tags.map(tag => String(tag).trim()).filter(Boolean).join(', ');
      }
      case 'images': {
        const raw = resolveInventoryValue(e, 'images');
        const images = Array.isArray(raw) ? raw : (typeof raw === 'string' ? raw.split(',') : []);
        const cleaned = images.map(img => String(img).trim()).filter(Boolean);
        return cleaned.length ? `${cleaned.length}` : '';
      }
      case 'status':
        return (
          <StatusBadge data-status={e.status?.toLowerCase() || 'dostępny'}>
            <DotIcon />
            {equipmentStatusLabel(t, e.status || 'Dostępny')}
          </StatusBadge>
        );
      case 'purchaseDate': {
        const val = resolveInventoryValue(e, 'purchaseDate');
        return val ? String(val).slice(0, 10) : '';
      }
      case 'price':
        return formatDailyPrice(e, t('priceBreakdown.perDay'));
      case 'maintenanceDate': {
        const val = resolveInventoryValue(e, 'maintenanceDate');
        return val ? String(val).slice(0, 10) : '';
      }
      case 'price_over_7': {
        const val = resolveInventoryValue(e, 'price_over_7') ?? resolveInventoryValue(e, 'priceOver7') ?? resolveInventoryValue(e, 'long_term_price') ?? resolveInventoryValue(e, 'cena7plus');
        if (val == null || val === '') return '';
        const num = Number(val) || 0;
        const formatted = new Intl.NumberFormat('pl-PL', { maximumFractionDigits: 2 }).format(num);
          const currency = resolveInventoryValue(e, 'currency') || resolveInventoryValue(e, 'priceCurrency') || resolveInventoryValue(e, 'price_currency') || 'PLN';
        return t('priceBreakdown.perDay').replace('{amount}', `${formatted} ${String(currency).toUpperCase()}`);
      }
      case 'bail': {
        let val = resolveInventoryValue(e, 'bail') ?? resolveInventoryValue(e, 'deposit') ?? resolveInventoryValue(e, 'kaucja');
        // additional fallbacks: pricing/display values sometimes contain deposit
        if (val == null || val === undefined) {
          if (e.pricing) val = e.pricing.deposit ?? e.pricing.deposit_amount ?? e.pricing.deposit_override ?? val;
        }
        if (val == null || val === undefined) {
          if (e.display_values) val = e.display_values.deposit ?? e.display_values.deposit_amount ?? val;
        }
        // If kaucja is missing, show 0.00 with the record currency so column is visible
        const num = Number(val ?? 0) || 0;
        const formatted = new Intl.NumberFormat('pl-PL', { maximumFractionDigits: 2 }).format(num);
        const currency = resolveInventoryValue(e, 'currency') || resolveInventoryValue(e, 'depositCurrency') || resolveInventoryValue(e, 'deposit_currency') || resolveInventoryValue(e, 'priceCurrency') || 'PLN';
        return `${formatted} ${String(currency).toUpperCase()}`;
      }
      case 'condition': {
        const val = resolveInventoryValue(e, 'condition');
        return val ?? '';
      }
      case 'location': {
        const val = resolveInventoryValue(e, 'location');
        return val ?? '';
      }
      case 'supplier': {
        const val = resolveInventoryValue(e, 'supplier');
        return val ?? '';
      }
      case 'sku': {
        const val = resolveInventoryValue(e, 'sku');
        return val ?? '';
      }
      case 'priceCurrency': {
        const val = resolveInventoryValue(e, 'priceCurrency');
        return val ?? '';
      }
      case 'depositCurrency': {
        const val = resolveInventoryValue(e, 'depositCurrency');
        return val ?? '';
      }
      case 'currency': {
        const val = resolveInventoryValue(e, 'currency') || resolveInventoryValue(e, 'priceCurrency') || resolveInventoryValue(e, 'depositCurrency');
        return val ?? '';
      }
      case 'wearLevel': {
        const val = resolveInventoryValue(e, 'wearLevel');
        return val ?? '';
      }
      case 'compatibility': {
        const val = resolveInventoryValue(e, 'compatibility');
        return val ?? '';
      }
      case 'sizeRange': {
        const val = resolveInventoryValue(e, 'sizeRange');
        return val ?? '';
      }
      default: {
        if (Object.prototype.hasOwnProperty.call(customFieldTypeByKey, colKey)) {
          return withEmptyDash(formatCustomCell(resolveInventoryValue(e, colKey), customFieldTypeByKey[colKey]));
        }
        return resolveInventoryValue(e, colKey) ?? e[colKey];
      }
    }
  };

  return (
    <Container>
      <DatepickerTheme />
      <HeaderRow>
        <TitleSection>
          <TitleIcon>
            <PackageIcon />
          </TitleIcon>
          <Title>{t('inventory.title')}</Title>
        </TitleSection>
        <ActionButtons data-tour="inventory-actions">
          <PrimaryButton onClick={handleOpenCalendar}>
            <CalendarIcon />
            {t('inventory.calendar')}
          </PrimaryButton>
          <SettingsButton onClick={() => navigate('/inventory/add-category')}>
            <GearIcon />
            {t('inventory.settings')}
          </SettingsButton>
          {/* Etykiety dla tego, co WIDAĆ: przy filtrach — przefiltrowanej listy, bez filtrów —
              całego magazynu (bez listy ID w adresie, który przy setkach sztuk puchłby). */}
          <SettingsButton
            onClick={() => navigate(activeFiltersCount > 0
              ? `/inventory/labels?ids=${filteredEquipment.map((e) => e.id).join(',')}`
              : '/inventory/labels')}
            disabled={!filteredEquipment.length}
            data-cy="inventory-labels"
          >
            <PrinterIcon size={16} />
            {t('labels.button')}
          </SettingsButton>
          <PrimaryButton onClick={() => navigate('/inventory/add')}>
            <PlusIcon />
            {t('inventory.add')}
          </PrimaryButton>
        </ActionButtons>
      </HeaderRow>
      {/* Pokaż alert błędu pod tytułem (auto-hide po 10s) */}
      {deleteError && (
        <ErrorBanner>
          <ErrorMsg>{deleteError}</ErrorMsg>
        </ErrorBanner>
      )}

      {/* DODAJ MAŁE LICZNIKI */}
      <StatsRow>
        <StatCard
          $clickable
          onClick={() => setFilters({ ...filters, status: 'Dostępny' })}
        >
          <StatIcon $tone="success">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </StatIcon>
          <StatContent>
            <StatValue>{stats.available}</StatValue>
            <StatLabel>{t('inventory.status.Dostępny')}</StatLabel>
          </StatContent>
        </StatCard>

        <StatCard
          $clickable
          onClick={() => setFilters({ ...filters, status: 'Wypożyczony' })}
        >
          <StatIcon>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </StatIcon>
          <StatContent>
            <StatValue>{stats.rented}</StatValue>
            <StatLabel>{t('inventory.status.Wypożyczony')}</StatLabel>
          </StatContent>
        </StatCard>

        <StatCard
          $clickable
          onClick={() => setFilters({ ...filters, status: 'W serwisie' })}
        >
          <StatIcon $tone="warning">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </StatIcon>
          <StatContent>
            <StatValue>{stats.service}</StatValue>
            <StatLabel>{t('inventory.status.W serwisie')}</StatLabel>
          </StatContent>
        </StatCard>
      </StatsRow>

      {/* ── Pasek filtrów (redesign 2026-09-22) ─────────────────────────
          Filtry leżą NA STRONIE, nie w modalu: przy ladzie zawęża się listę, patrząc na
          listę, a nie na okno, które ją zasłania. Nie ma też przycisku „Zastosuj filtry" —
          filtrowanie zawsze działało na żywo, a ten przycisk wyłącznie zamykał modal.
          Wymiary pochodzą z konfiguracji pól magazynu TEJ organizacji, patrz
          `visibleFilterDimensions`. */}
      <FilterBar data-tour="inventory-filters">
        <FilterBarTop>
          <FilterSearch>
            <SearchIcon />
            <input
              data-cy="inventory-search"
              type="search"
              // Numery inwentarzowe i marki to nie są słowa — sprawdzanie pisowni podkreśla
              // tu na czerwono niemal każde zapytanie.
              spellCheck={false}
              autoComplete="off"
              placeholder={t('inventory.searchPlaceholder')}
              aria-label={t('inventory.searchPlaceholder')}
              value={searchDebounce}
              onChange={e => setSearchDebounce(e.target.value)}
            />
          </FilterSearch>
          <ScanSources onScan={handleScan} compact dataCyPrefix="inventory-scan" />
          {lastScan && (
            <ScanBadge data-cy="inventory-scan-badge" title={lastScan}>
              <BarcodeIcon />
              {t('inventory.scanned').replace('{code}', lastScan)}
            </ScanBadge>
          )}
          {!loading && (
            <FilterResults data-cy="inventory-results-count">
              {activeFiltersCount > 0
                ? t('inventory.resultsOfTotal')
                    .replace('{shown}', String(filteredEquipment.length))
                    .replace('{total}', String(equipment.length))
                : t('inventory.resultsTotal').replace('{total}', String(filteredEquipment.length))}
            </FilterResults>
          )}
        </FilterBarTop>

        <FilterPills>
          {visibleFilterDimensions.map((dim) => {
            const etykieta = columnLabel(t, dim);
            const wartosc = activeFilterSummary(dim.key, filters, { formatDate: formatFilterDate });
            const wspolne = {
              key: dim.key,
              label: etykieta,
              value: wartosc,
              onClear: () => handleClearOneFilter(dim.key),
              searchPlaceholder: t('inventory.filterSearchPlaceholder'),
              emptyLabel: t('inventory.filterNoMatches'),
              clearLabel: t('inventory.filterClearOne').replace('{name}', etykieta),
              'data-cy': `inventory-filter-${dim.key}`,
            };

            // Zakres dostępności — jedyny wymiar bez listy wartości; kalendarz w dymku.
            if (dim.kind === 'availability') {
              return (
                <FilterPill {...wspolne} options={[]}>
                  <PillDateSlot>
                    <DatePicker
                      selected={filters.dateFrom}
                      onChange={(dates) => {
                        const [od, doD] = dates || [null, null];
                        setFilters({ ...filters, dateFrom: od, dateTo: doD });
                        setPage(1);
                      }}
                      startDate={filters.dateFrom}
                      endDate={filters.dateTo}
                      selectsRange
                      inline
                      locale="pl"
                    />
                  </PillDateSlot>
                </FilterPill>
              );
            }

            // Rozmiar: lista konkretnych wartości ORAZ zakres od–do (jedyny taki wymiar).
            if (dim.kind === 'size') {
              return (
                <FilterPill
                  {...wspolne}
                  options={filterOptionsByKey.size || []}
                  onSelect={(v) => { setFilters({ ...filters, size: v, minSize: '', maxSize: '' }); setPage(1); }}
                  optionsLabel={t('inventory.sizePickOne')}
                >
                  <InlineRangeEditor
                    minValue={filters.minSize}
                    maxValue={filters.maxSize}
                    caption={t('inventory.sizeRange')}
                    labelMin={t('inventory.rangeFrom')}
                    labelMax={t('inventory.rangeTo')}
                    placeholderMin={t('inventory.rangeMinPlaceholder')}
                    placeholderMax={t('inventory.rangeMaxPlaceholder')}
                    onApply={({ min, max }) => { setFilters({ ...filters, minSize: min, maxSize: max, size: '' }); setPage(1); }}
                  />
                </FilterPill>
              );
            }

            if (dim.kind === 'date') {
              return (
                <FilterPill {...wspolne} options={[]}>
                  <PillDateSlot>
                    <SearchInput
                      type="date"
                      aria-label={etykieta}
                      value={(filters.dynamic || {})[dim.key] || ''}
                      onChange={(e) => { setDynamicFilter(dim.key, e.target.value); setPage(1); }}
                    />
                  </PillDateSlot>
                </FilterPill>
              );
            }

            if (dim.kind === 'number') {
              return (
                <FilterPill {...wspolne} options={[]}>
                  <PillDateSlot>
                    <SearchInput
                      type="number"
                      aria-label={etykieta}
                      placeholder={etykieta}
                      value={(filters.dynamic || {})[dim.key] || ''}
                      onChange={(e) => { setDynamicFilter(dim.key, e.target.value); setPage(1); }}
                      onWheel={(e) => e.currentTarget.blur()}
                    />
                  </PillDateSlot>
                </FilterPill>
              );
            }

            // Reszta — lista wartości obecnych w danych. Status/Typ idą tą samą ścieżką co
            // marka, klasa i każde pole własne: jeden rodzaj kontrolki, jeden sposób czyszczenia.
            const naWierzchu = TOP_LEVEL_FILTER_KEYS.has(dim.key);
            return (
              <FilterPill
                {...wspolne}
                options={filterOptionsByKey[dim.key] || []}
                onSelect={(v) => {
                  if (naWierzchu) setFilters({ ...filters, [dim.key]: v });
                  else setDynamicFilter(dim.key, v);
                  setPage(1);
                }}
              />
            );
          })}

          {activeFiltersCount > 0 && (
            <FilterClearAll type="button" data-cy="inventory-clear-filters" onClick={handleClearFilters}>
              {t('inventory.clearFilters')}
            </FilterClearAll>
          )}
        </FilterPills>
      </FilterBar>

      {selected.length > 0 && (
        <SelectionActionBar>
          <SelectionMeta>
            {t('inventory.selectedCount')}: <strong>{selected.length}</strong>
          </SelectionMeta>
          <SelectionActions>
            {selected.length === 1 && (
              <SecondaryButton onClick={handleEditSelected}>
                {t('equipmentDetails.edit')}
              </SecondaryButton>
            )}
            <SecondaryButton
              // `selected` trzyma INDEKSY wierszy bieżącej strony, nie identyfikatory sprzętu.
              onClick={() => navigate(`/inventory/labels?ids=${selected.map((i) => paginatedEquipment[i]?.id).filter(Boolean).join(',')}`)}
              data-cy="inventory-print-labels"
            >
              <PrinterIcon size={16} />
              {t('labels.printSelected').replace('{n}', String(selected.length))}
            </SecondaryButton>
            <DangerButton onClick={handleDelete}>
              <TrashIcon />
              {t('inventory.delete')} ({selected.length})
            </DangerButton>
            <SecondaryButton onClick={handleClearSelection}>
              {t('common.clear')}
            </SecondaryButton>
          </SelectionActions>
        </SelectionActionBar>
      )}

      <TableContainer style={{ position: 'relative' }}>
        {loading && (
          <LoaderOverlay>
            <Spinner />
          </LoaderOverlay>
        )}
        <TableWrapper>
        {paginatedEquipment.length > 0 ? (
          <Table>
            <thead>
              <tr>
                <Th style={{ width: 40 }}></Th>
                {kolumnyTabeli.map(col => {
                  const tooltips = {
                    maintenanceDate: t('inventory.tooltip.maintenanceDate'),
                    purchaseDate: t('inventory.tooltip.purchaseDate'),
                    condition: t('inventory.tooltip.condition'),
                    location: t('inventory.tooltip.location'),
                    wearLevel: t('inventory.tooltip.wearLevel'),
                    compatibility: t('inventory.tooltip.compatibility')
                  };
                  const tooltip = tooltips[col.key];

                  return (
                    <ThWithTooltip
                      key={col.key}
                      style={col.width ? { width: col.width } : {}}
                    >
                      <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                        {columnLabel(t, col)}
                        <SortButton
                          onClick={() => handleSort(col.key)}
                          data-active={sort.key === col.key && sort.direction !== 'none'}
                          /* Przycisk z sama strzalka nie mowi nic ani czytnikowi ekranu, ani
                             mysza: dymek wyjasnia teraz, ktorej kolumny dotyczy sortowanie. */
                          aria-label={`${t('inventory.sortBy')} ${columnLabel(t, col)}`}
                          title={`${t('inventory.sortBy')} ${columnLabel(t, col)}`}
                        >
                          {sort.key === col.key ? sortIcons[sort.direction] : sortIcons.none}
                        </SortButton>
                      </span>
                      {tooltip && <Tooltip>{tooltip}</Tooltip>}
                    </ThWithTooltip>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {paginatedEquipment.map((e, idx) => (
                <EquipmentTableRow
                  key={e.id || idx}
                  even={idx % 2 === 0}
                  selected={selected.includes(idx)}
                  onClick={() => navigate(`/inventory/${e.id}`)}
                >
                  <SelectionCell>
                    <SelectionToggleButton
                      type="button"
                      onClick={ev => {
                        ev.stopPropagation();
                        handleSelect(idx);
                      }}
                      aria-label={selected.includes(idx)
                        ? (t('inventory.unselectItem'))
                        : (t('inventory.selectItem'))}
                      title={selected.includes(idx)
                        ? (t('inventory.unselectItem'))
                        : (t('inventory.selectItem'))}
                    >
                      <EquipmentCheckbox
                        type="checkbox"
                        checked={selected.includes(idx)}
                        readOnly
                        tabIndex={-1}
                      />
                    </SelectionToggleButton>
                  </SelectionCell>

                  {kolumnyTabeli.map(col => (
                    <Td key={col.key} data-col={col.key}>{renderCell(col.key, e)}</Td>
                  ))}
                </EquipmentTableRow>
              ))}
            </tbody>
          </Table>
        ) : (
          <EmptyState>
            <div className="icon">
              <PackageIcon />
            </div>
            <div className="text">
              {filteredEquipment.length === 0 && equipment.length > 0
                ? (t('inventory.noResults'))
                : (t('inventory.emptyState'))}
            </div>
            {filteredEquipment.length === 0 && equipment.length > 0 && activeFiltersCount > 0 && (
              <div style={{ marginTop: '12px' }}>
                <SecondaryButton onClick={handleClearFilters}>
                  {t('inventory.clearFiltersToSeeAll')}
                </SecondaryButton>
              </div>
            )}
            {equipment.length === 0 && (
              <>
                <div className="text" style={{ fontSize: '0.9rem', fontWeight: 400, marginTop: '4px' }}>
                  {t('inventory.emptyState.hint')}
                </div>
                <div style={{ marginTop: '16px', display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                  <PrimaryButton data-cy="inventory-add-button" onClick={() => navigate('/inventory/add')}>
                    {t('inventory.add')}
                  </PrimaryButton>
                  {isAdminUser && (
                    <SecondaryButton data-cy="inventory-import-button" onClick={() => navigate('/import?entity=equipment')}>
                      {t('inventory.importFromFile')}
                    </SecondaryButton>
                  )}
                </div>
              </>
            )}
          </EmptyState>
        )}
        </TableWrapper>
      </TableContainer>

      <PaginationCompact
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        pageSize={pageSize}
        totalItems={sortedEquipment.length}
      />

      {deletedItems.length > 0 && (
        <UndoNotification>
          <div>
            {t('inventory.deletionScheduled')}
          </div>
          <UndoButton onClick={handleUndo}>
            {t('common.undo')}
          </UndoButton>
        </UndoNotification>
      )}
    </Container>
  );
}
